import React, { useState, useEffect } from 'react';
import { LatexText, TikzDiagram } from './LatexUtils';

const SIKLABELS = ['A', 'B', 'C', 'D', 'E'];
const ZORLUK_RENK = { kolay: '#4CAF50', orta: '#FF9800', zor: '#F44336', uzman: '#9C27B0' };
const ZORLUK_TR = { kolay: 'Kolay', orta: 'Orta', zor: 'Zor', uzman: 'Uzman' };

function SoruBlok({ soru, siraNo, cevaplarAcik, seciliCevap, onCevapSec }) {
  const siklar = [soru.sik_a, soru.sik_b, soru.sik_c, soru.sik_d, soru.sik_e];
  const dogru = soru.dogru_cevap; // 'A'–'E'
  const dogruIndex = dogru ? dogru.charCodeAt(0) - 65 : -1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
      {/* Başlık şeridi */}
      <div className="flex items-center justify-between px-5 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold text-sm">{siraNo}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{soru.konu}</span>
          {soru.alt_konu && <span className="text-xs text-gray-400">/ {soru.alt_konu}</span>}
        </div>
        <div className="flex items-center gap-2">
          {soru.kazanim_kodu && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-mono">{soru.kazanim_kodu}</span>
          )}
          {soru.zorluk && (
            <span className="text-[10px] px-2 py-0.5 rounded font-semibold text-white" style={{ backgroundColor: ZORLUK_RENK[soru.zorluk] || '#757575' }}>
              {ZORLUK_TR[soru.zorluk] || soru.zorluk}
            </span>
          )}
          {soru.adim_sayisi && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-600">{soru.adim_sayisi} adım</span>
          )}
        </div>
      </div>

      {/* Soru metni — B4: kapanis cumlesi diyagramdan SONRA */}
      <div className="px-6 pt-5 pb-3 text-base leading-relaxed text-gray-900 font-serif">
        {soru.soru_metni_latex
          ? soru.diyagram_tikz
            ? (() => {
                const kapanis_re = /\\textbf\s*\{(?:Buna\s+g[oö]re|Bu\s+durumda|Verilenlere\s+g[oö]re)[^]*\}[^}]*$/i;
                const metin = soru.soru_metni_latex || '';
                const km = kapanis_re.exec(metin);
                const giris = km ? metin.slice(0, km.index) : metin;
                const kapanis = km ? km[0] : '';
                return (
                  <>
                    <LatexText>{giris}</LatexText>
                    <TikzDiagram tikzCode={soru.diyagram_tikz} />
                    {kapanis && <div className="mt-2"><LatexText>{kapanis}</LatexText></div>}
                  </>
                );
              })()
            : <LatexText>{soru.soru_metni_latex}</LatexText>
          : <span className="text-gray-400 italic">(soru metni yok)</span>
        }
      </div>

      {/* Şıklar */}
      <div className="px-6 pb-5 space-y-2">
        {siklar.map((sik, i) => {
          if (!sik) return null;
          const label = SIKLABELS[i];
          const secildi = seciliCevap === label;
          const dogruSik = cevaplarAcik && i === dogruIndex;
          const yanlisSec = cevaplarAcik && secildi && !dogruSik;

          let bg = 'bg-gray-50 border-gray-200 text-gray-800';
          if (dogruSik) bg = 'bg-green-50 border-green-500 text-green-800';
          else if (yanlisSec) bg = 'bg-red-50 border-red-400 text-red-700';
          else if (secildi) bg = 'bg-blue-50 border-blue-500 text-blue-800';

          return (
            <button
              key={label}
              onClick={() => !cevaplarAcik && onCevapSec(label)}
              className={`w-full flex items-start gap-3 px-4 py-2.5 rounded-lg border text-left transition-colors ${bg} ${!cevaplarAcik ? 'hover:bg-blue-50 hover:border-blue-400 cursor-pointer' : 'cursor-default'}`}
            >
              <span className="font-bold text-sm min-w-[1.2rem]">{label})</span>
              <span className="text-sm leading-relaxed font-serif flex-1">
                <LatexText>{sik}</LatexText>
              </span>
              {cevaplarAcik && dogruSik && <span className="ml-auto text-green-600 text-lg font-bold">✓</span>}
              {yanlisSec && <span className="ml-auto text-red-500 text-lg font-bold">✗</span>}
            </button>
          );
        })}
      </div>

      {/* Çözüm (cevaplar açıkken) */}
      {cevaplarAcik && soru.cozum_adimlari_latex && (
        <div className="mx-6 mb-5 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">Çözüm</div>
          <div className="text-sm text-gray-800 font-serif leading-relaxed">
            <LatexText>{soru.cozum_adimlari_latex}</LatexText>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TestGorunum({ apiUrl, partisi, baslik, onGeri, authFetch }) {
  const [sorular, setSorular] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [cevaplarAcik, setCevaplarAcik] = useState(false);
  const [seciliCevaplar, setSeciliCevaplar] = useState({}); // { soruId: 'A' }
  const [detaylar, setDetaylar] = useState({}); // { soruId: fullRow }
  const [hata, setHata] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setYukleniyor(true);
      try {
        const r = await authFetch(`${apiUrl}/api/sorular?uretim_partisi=${encodeURIComponent(partisi)}&limit=50&siralama=tarih`);
        const data = await r.json();
        const liste = data.sorular || [];
        setSorular(liste);

        // Tüm soruların detayını çek (soru_metni_latex, şıklar vs.)
        const detayMap = {};
        await Promise.all(liste.map(async (s) => {
          const dr = await authFetch(`${apiUrl}/api/sorular/${s.id}`);
          const d = await dr.json();
          detayMap[s.id] = d;
        }));
        if (!cancelled) setDetaylar(detayMap);
      } catch (e) {
        if (!cancelled) setHata(e.message);
      }
      if (!cancelled) setYukleniyor(false);
    })();
    return () => { cancelled = true; };
  }, [apiUrl, partisi, authFetch]);

  const onCevapSec = (soruId, label) => {
    setSeciliCevaplar(prev => ({ ...prev, [soruId]: label }));
  };

  // Skor hesapla
  const dogru = sorular.filter(s => {
    const d = detaylar[s.id];
    return d && seciliCevaplar[s.id] && seciliCevaplar[s.id] === d.dogru_cevap;
  }).length;
  const cevaplanan = Object.keys(seciliCevaplar).length;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Test Başlık */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-blue-600">
        <div>
          <button onClick={onGeri} className="text-blue-600 text-sm hover:underline mb-1 block">← Geri</button>
          <h2 className="text-xl font-bold text-gray-800">{baslik || partisi}</h2>
          {!yukleniyor && (
            <p className="text-sm text-gray-500 mt-0.5">
              {sorular.length} soru
              {cevaplanan > 0 && !cevaplarAcik && ` · ${cevaplanan} cevaplanmış`}
              {cevaplarAcik && ` · ${dogru}/${sorular.length} doğru`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {!cevaplarAcik && cevaplanan > 0 && (
            <button
              onClick={() => setCevaplarAcik(true)}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
            >
              Cevapları Göster
            </button>
          )}
          {cevaplarAcik && (
            <div className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">
              Skor: {dogru}/{sorular.length}
            </div>
          )}
          {cevaplarAcik && (
            <button
              onClick={() => { setCevaplarAcik(false); setSeciliCevaplar({}); }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
            >
              Sıfırla
            </button>
          )}
          {!cevaplarAcik && (
            <button
              onClick={() => setCevaplarAcik(true)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cevap Anahtarı
            </button>
          )}
        </div>
      </div>

      {hata && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg mb-4">Yükleme hatası: {hata}</div>
      )}

      {yukleniyor ? (
        <div className="text-center py-16 text-gray-400">Sorular yükleniyor...</div>
      ) : sorular.length === 0 ? (
        <div className="text-center py-16 text-gray-400">Bu partide soru bulunamadı.</div>
      ) : (
        <div>
          {sorular.map((s, i) => {
            const detay = detaylar[s.id] || s;
            return (
              <SoruBlok
                key={s.id}
                soru={detay}
                siraNo={i + 1}
                cevaplarAcik={cevaplarAcik}
                seciliCevap={seciliCevaplar[s.id]}
                onCevapSec={(label) => onCevapSec(s.id, label)}
              />
            );
          })}
        </div>
      )}

      {/* Cevap anahtarı özet tablosu */}
      {sorular.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
            {cevaplarAcik ? 'Cevap Anahtarı' : 'Cevap Durumu'}
          </div>
          <div className="flex flex-wrap gap-2">
            {sorular.map((s, i) => {
              const detay = detaylar[s.id];
              const sec = seciliCevaplar[s.id];
              const dogruCevap = detay?.dogru_cevap;
              let bg = 'bg-gray-200 text-gray-600';
              if (sec && !cevaplarAcik) bg = 'bg-blue-500 text-white';
              if (cevaplarAcik && sec && sec === dogruCevap) bg = 'bg-green-500 text-white';
              if (cevaplarAcik && sec && sec !== dogruCevap) bg = 'bg-red-500 text-white';
              return (
                <div key={s.id} className={`w-10 h-10 flex flex-col items-center justify-center rounded-lg text-xs font-bold ${bg}`}>
                  <span className="text-[10px] opacity-70">{i + 1}</span>
                  <span>{cevaplarAcik ? (dogruCevap || '?') : (sec || '—')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
